import { useQueryClient } from '@tanstack/react-query';

import {
  getParceirosAdminControllerListarQueryKey,
  useParceirosAdminControllerEnviarLogo,
} from '@/api/generated/admin-parceiros/admin-parceiros';
import type { ParceiroAdminResponse } from '@/api/generated/model';
import { ApiError } from '@/api/http-client';
import { CampoDeImagem } from '@/shared/components/CampoDeImagem';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { toast } from '@/shared/ui/sonner';

/**
 * Envio do logo do parceiro.
 *
 * ⚠️ Só o logo, e a capa NÃO voltou a ser oferecida de propósito: por não ter
 * proporção definida, cada card da vitrine recortava a capa de um jeito e a
 * grade saía desalinhada. Hoje o logo é o que aparece em todo lugar — card,
 * detalhe e carrossel da sidebar —, sempre no mesmo quadro 3:1. Um segundo
 * ativo aqui só serviria para reintroduzir aquela divergência.
 *
 * ⚠️ A regra de proporção (3:1) é do SERVIDOR e a mensagem de erro vem de lá
 * pronta, citando o que foi enviado e o que se espera. O texto de ajuda aqui é
 * orientação; a validação nunca é do cliente, porque `accept="image/*"` não
 * impede um POST direto.
 *
 * O que o `recorte` faz é tirar essa recusa do caminho de quem usa a tela: um
 * arquivo 1125×400 não volta mais com "esta posição exige 3:1" — ele abre no
 * enquadramento e sai de lá em 1200×400.
 *
 * `encaixar` porque isto é LOGO: os empilhados (marca em cima, nome embaixo)
 * chegam perto de 2:1 e recortar em 3:1 decepa a marca ou a assinatura. O
 * diálogo abre com ele inteiro no quadro, sobrando fundo nas laterais — e quem
 * quiser preencher ainda pode, é só aumentar o zoom.
 */
export function ImagensDoParceiroDialogo({
  aberto,
  aoFechar,
  parceiro,
}: {
  aberto: boolean;
  aoFechar: () => void;
  parceiro: ParceiroAdminResponse;
}) {
  const queryClient = useQueryClient();
  const enviarLogo = useParceirosAdminControllerEnviarLogo();

  return (
    <Dialog open={aberto} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Logo de {parceiro.nome}</DialogTitle>
          <DialogDescription>
            É o que aparece no card da vitrine, na página do parceiro e no carrossel da sidebar.
          </DialogDescription>
        </DialogHeader>

        <CampoDeImagem
          rotulo="Logo"
          ajuda="PNG ou JPG em qualquer proporção — abre inteiro num quadro 3:1 e você ajusta. Até 2 MB."
          urlAtual={parceiro.logoUrl}
          proporcao="aspect-[3/1]"
          recorte={{ proporcao: 3, larguraSaida: 1200, encaixar: true }}
          enviando={enviarLogo.isPending}
          aoEnviar={(arquivo) =>
            enviarLogo.mutate(
              { id: parceiro.id, data: { arquivo } },
              aoResponder(queryClient, 'Logo atualizado'),
            )
          }
        />

        <DialogFooter>
          <Button variant="outline" onClick={aoFechar}>
            Fechar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function aoResponder(queryClient: ReturnType<typeof useQueryClient>, sucesso: string) {
  return {
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: getParceirosAdminControllerListarQueryKey(),
      });
      toast.success(sucesso);
    },
    // A mensagem do servidor já diz a regra ("A imagem tem 800×400 (2.00:1).
    // Esta posição exige 3:1 — por exemplo 1200×400."). Reescrevê-la aqui
    // criaria dois textos para divergir.
    onError: (erro: unknown) =>
      toast.error(erro instanceof ApiError ? erro.message : 'Não foi possível enviar a imagem'),
  };
}
